import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { Colors, Durations, Radii, Spacing } from "@paclife/plds.tokens";

import Typography from "common/Typography/Typography";

const Nav = styled.nav`
    display: flex;
    /** Future Feature: handle scroll for many tabs */
    flex-wrap: nowrap;
    margin-bottom: ${Spacing[16]};
`;

const Text = styled(Typography)`
    color: ${({ active }) => (active ? Colors.neutral1000 : Colors.neutral500)};
    white-space: nowrap;
`;

const Box = styled.div`
    border: 2px solid;
    border-color: ${({ active }) =>
        active ? Colors.neutral1000 : Colors.neutral500};
    border-radius: ${Radii.rounded};
    width: 33px;
    height: 32px;
    margin-left: ${Spacing[8]};
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Count = styled(Typography)`
    color: ${({ active }) => (active ? Colors.neutral1000 : Colors.neutral500)};
`;

const Tab = styled(Link)`
    text-decoration: none;
    margin-right: ${Spacing[32]};
    position: relative;
    display: flex;

    &:after {
        content: "";
        position: absolute;
        width: ${({ active }) => (active ? "48px" : "0px")};
        height: 4px;
        background: ${Colors.blue500};
        bottom: -8px;
        left: 0;
        transition: width ${Durations.long}s ease-in-out;
    }

    &:hover {
        text-decoration: none;
        ${Text}, ${Count} {
            color: ${Colors.neutral700};
        }
        ${Box} {
            border-color: ${Colors.neutral700};
        }
        :after {
            width: 48px;
            transition: width ${Durations.long}s ease-in-out;
        }
    }
`;

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const TabMenu = ({ tabs, counts: countsProp }) => {
    const { pathname } = useLocation();
    const queryParams = useQuery().get(tabs[0].cat);
    const activeTab = ({ path, query, defaultActive }) => {
        if (path) {
            return pathname === path;
        }
        if (queryParams) {
            return queryParams === query;
        } else {
            return defaultActive;
        }
    };
    const [counts, setCounts] = useState(countsProp);

    useEffect(() => {
        setCounts(countsProp);
    }, [countsProp]);

    return (
        <Nav>
            {tabs.map(({ name, path, cat, query, defaultActive }) => {
                return (
                    <Tab
                        key={name}
                        to={path || `?${cat}=${query}`}
                        active={activeTab({ path, query, defaultActive })}
                        data-track="analytics"
                        data-bname={`${name}-tab`}
                    >
                        <Text
                            variant="h3"
                            active={activeTab({ path, query, defaultActive })}
                        >
                            {name}
                        </Text>
                        {counts && counts[query] > 0 && (
                            <Box
                                active={activeTab({
                                    path,
                                    query,
                                    defaultActive
                                })}
                            >
                                <Count
                                    variant="caption"
                                    bold
                                    active={activeTab({
                                        path,
                                        query,
                                        defaultActive
                                    })}
                                >
                                    {counts[query]}
                                </Count>
                            </Box>
                        )}
                    </Tab>
                );
            })}
        </Nav>
    );
};

export default TabMenu;

TabMenu.propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            cat: PropTypes.string,
            query: PropTypes.string,
            path: PropTypes.string,
            defaultActive: PropTypes.bool
        })
    ).isRequired,
    counts: PropTypes.object
};

TabMenu.defaultProps = {
    counts: undefined
};
